import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { SERVER } from '../config/config';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaCheck, FaArrowRight, FaSpinner } from 'react-icons/fa';
import { LuFacebook } from "react-icons/lu";
import { FaInstagram } from "react-icons/fa6";
import { PiTiktokLogoThin } from "react-icons/pi";




const BookConsultation = () => {
  const location = useLocation();
  const [selectedServices, setSelectedServices] = useState(['wetekup']); // Changed to array
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    // Scroll to top when component mounts or location changes
    window.scrollTo(0, 0);
  }, [location]);
  const [formData, setFormData] = useState({
    name: '',
    jobPosition: '',
    email: '',
    phone: '',
    activityField: '',
    company: '',
    description: '',
    productPhoto: null,
    socialMediaLinks: {
      facebook: '',
      instagram: '',
      website: ''
    },
    detailedNeeds: '',
    // WeCapture specific fields
    studioNeeds: '',
    modelIncluded: '',
    shootingType: '',
    shootingLocation: [],
    shootingDate: '',
    preferredContact: 'email'
  });

  const services = {
    wetekup: {
      name: 'We Tekup',
      icon: '/wetekuplogo.svg',
      color: '#1D44E9',
      services: ['Websites', 'Branding', 'Mobile Apps'],
      activityFields: [
        'Technology',
        'Healthcare',
        'Finance',
        'E-commerce',
        'Education',
        'Real Estate',
        'Food & Beverage',
        'Fashion',
        'Consulting',
        'Other'
      ]
    },
    wecapture: {
      name: 'We Capture',
      icon: '/wetekuplogo.svg',
      color: '#1D44E9',
      services: ['Photography', 'Editing', 'Videography'],
      shootingTypes: [
        'Product shooting',
        'Portrait shooting',
        'Event shooting',
        'Corporate shooting',
        'Fashion shooting',
        'Food shooting',
        'Architecture shooting'
      ]
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else if (name.startsWith('socialMedia.')) {
      const platform = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMediaLinks: {
          ...prev.socialMediaLinks,
          [platform]: value
        }
      }));
    } else if (name === 'shootingLocation') {
      const currentLocations = formData.shootingLocation || [];
      if (currentLocations.includes(value)) {
        setFormData(prev => ({
          ...prev,
          shootingLocation: currentLocations.filter(loc => loc !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          shootingLocation: [...currentLocations, value]
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleServiceChange = (service) => {
    setSelectedServices(prev => {
      if (prev.includes(service)) {
        // Remove service if already selected (but keep at least one)
        return prev.length > 1 ? prev.filter(s => s !== service) : prev;
      } else {
        // Add service to selection
        return [...prev, service];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    const consultationData = {
      ...formData,
      services: selectedServices, // Changed to array
      // Ensure empty date is sent as null instead of empty string
      shootingDate: formData.shootingDate && formData.shootingDate.trim() !== '' ? formData.shootingDate : null
    };

    try {
      const response = await axios.post(`${SERVER}/consultations`, consultationData);
      
      if (response.status === 201) {
        setSubmitMessage('success');
        
        // Reset form
        setFormData({
          name: '',
          jobPosition: '',
          email: '',
          phone: '',
          activityField: '',
          company: '',
          description: '',
          productPhoto: null,
          socialMediaLinks: {
            facebook: '',
            instagram: '',
            website: ''
          },
          detailedNeeds: '',
          studioNeeds: '',
          modelIncluded: '',
          shootingType: '',
          shootingLocation: [],
          shootingDate: '',
          preferredContact: 'email'
        });
        
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      setSubmitMessage('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show fields for all selected services
  const showWeTekupFields = selectedServices.includes('wetekup');
  const showWeCaptureFields = selectedServices.includes('wecapture');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-12 md:-ml-60 pb-16">
        <div className="max-w-5xl px-3 md:px-10 mx-auto">
          {/* Success/Error Messages */}
          {submitMessage === 'success' && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <FaCheck className="text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-green-800 font-semibold ">Thank you for your consultation request!</h3>
                  <p className="text-green-700 text-sm mt-1">
                    We'll review your project details and get back to you within 24 hours.
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitMessage === 'error' && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">!</span>
                </div>
                <div>
                  <h3 className="text-red-800 font-semibold">Something went wrong</h3>
                  <p className="text-red-700 text-sm mt-1">
                    Please try again or contact us directly at info@wetekup.com
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header Section */}
          <div className="text-left mb-4 md:mb-16 w-full">
            <h1 className="text-2xl md:text-[32px] font-bold text-gray-900 mb-4 md:mb-6">
              Book a consultation
            </h1>
            <p className="text-base md:text-lg text-gray-500 mb-4 md:mb-12 max-w-3xl">
              Share your project with us. We’ll guide you every step of the way
            </p>

            {/* Service Selection Cards - Centered */}
            <div className="flex justify-center mb-4 md:mb-6">
              <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-6 w-full md:max-w-3xl">
                {Object.entries(services).map(([key, service]) => (
                  <div
                    key={key}
                    onClick={() => handleServiceChange(key)}
                    className={`relative px-2 py-3 md:py-8 rounded-2xl border-2 cursor-pointer transition-all duration-300 hover:shadow-md ${
                      selectedServices.includes(key)
                        ? 'border-primary-blue shadow-xl'
                        : 'border-gray-200 hover:border-gray-300'
                    } md:flex-1 md:min-w-0`}
                  >
                   
                    

                    {/* Service Header */}
                    <div className="flex flex-col md:flex-row items-center justify-center gap-2 mb-6">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                      >
                        <img src={service.icon} alt="" />
                      </div>
                      <h3 
                        className="text-xl"
                      >
                        <span className="font-normal">We</span>
                        <span className="font-bold">{key === 'wetekup' ? 'Tekup' : 'Capture'}</span>
                      </h3>
                    </div>

                    {/* Service List - 2 columns on mobile, flex-wrap on desktop */}
                    <div className="flex flex-wrap gap-1 md:gap-2">
                      {service.services.map((item, index) => (
                        <div key={index} className="flex items-center bg-soft-gray px-2 md:px-3 py-1.5 md:py-2 rounded-full gap-1 md:gap-2">
                          <div 
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: service.color }}
                          />
                          <span className="text-primary-gray text-[9px] md:text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Consultation Form */}
          <div className="rounded-3xl p-2 md:px-12">
            <div className="max-w-4xl">
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                    placeholder="First name"
                  />
                </div>

                {/* Job Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job position
                  </label>
                  <input
                    type="text"
                    name="jobPosition"
                    value={formData.jobPosition}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                    placeholder="Your job position"
                  />
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                      placeholder="Write your address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                      placeholder="Write your address"
                    />
                  </div>
                </div>

                {/* Activity Field and Company Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Activity field
                    </label>
                    <select
                      name="activityField"
                      value={formData.activityField}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white text-base"
                    >
                      <option value="">Select activity field</option>
                      {showWeTekupFields && services.wetekup.activityFields?.map((field, index) => (
                        <option key={index} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                      placeholder="Company name"
                    />
                  </div>
                </div>

                {/* Mini Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mini description of your project or company
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical text-base"
                    placeholder="Write text here"
                  />
                </div>

                {/* Product Photo Upload */}
                <div className="flex items-center gap-2 md:gap-8">
                  <div className="flex h-36 md:h-40 flex-col items-center bg-soft-gray py-6 px-4 rounded-xl border border-soft-gray">
                    <div className="w-14 h-14  bg-[#eeeeee] rounded-full p-4  flex items-center justify-center mb-2 cursor-pointer hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        name="productPhoto"
                        onChange={handleInputChange}
                        accept="image/*"
                        className="hidden"
                        id="productPhoto"
                      />
                      <label htmlFor="productPhoto" className="cursor-pointer">
                        <svg className="w-10 h-10 text-primary-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Import your product<br />Or service photo
                    </p>
                  </div>
                  
                  <div className="flex h-36 md:h-40 flex-col items-center bg-soft-gray py-6 px-4 border-2 border-dashed border-gray-300 rounded-xl">
                    <div className="w-20 h-20  rounded-lg flex items-center justify-center">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-400 text-center -mt-2">
                      You can add up to 10<br />pictures
                    </p>
                  </div>
                </div>

                {/* Social Media Links */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Social media links
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LuFacebook size={20} className='text-primary-gray'/>
                      </div>
                      <input
                        type="url"
                        name="socialMedia.facebook"
                        value={formData.socialMediaLinks.facebook}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-6 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                        placeholder="URL"
                      />
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaInstagram size={20} className='text-primary-gray'/>
 
                      </div>
                      <input
                        type="url"
                        name="socialMedia.instagram"
                        value={formData.socialMediaLinks.instagram}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-6 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                        placeholder="URL"
                      />
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PiTiktokLogoThin size={20} className='text-primary-gray' />

                      </div>
                      <input
                        type="url"
                        name="socialMedia.website"
                        value={formData.socialMediaLinks.website}
                        onChange={handleInputChange}
                        className="w-full pl-12 pr-6 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                        placeholder="URL"
                      />
                    </div>
                  </div>
                </div>

                {/* Detailed Needs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please describe your needs in details
                  </label>
                  <textarea
                    name="detailedNeeds"
                    value={formData.detailedNeeds}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical text-base"
                    placeholder="Describe your needs..."
                  />
                </div>

                {/* WeCapture Studio Details */}
                {showWeCaptureFields && (
                  <div className="border-t pt-8 mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">We capture Studio details</h3>
                    
                    {/* What do you need? */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        What do you need?
                      </label>
                      <div className="flex flex-col md:flex-row gap-4">
                        {['Photo only', 'Video only', 'Both'].map((option) => (
                          <label key={option} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="studioNeeds"
                              value={option}
                              checked={formData.studioNeeds === option}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Model/Mannequin included? */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Model / Mannequin included?
                      </label>
                      <div className="flex flex-col md:flex-row gap-4">
                        {['Yes, included', 'No, not needed', 'Yes, but I need you to provide'].map((option) => (
                          <label key={option} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="modelIncluded"
                              value={option}
                              checked={formData.modelIncluded === option}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Type of shooting */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type of shooting
                      </label>
                      <div className="relative">
                        <select
                          name="shootingType"
                          value={formData.shootingType}
                          onChange={handleInputChange}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white text-base"
                        >
                          <option value="">Select shooting type</option>
                          {services.wecapture.shootingTypes?.map((type, index) => (
                            <option key={index} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Preferred shooting location */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Preferred shooting location
                      </label>
                      <div className="flex flex-col md:flex-row gap-4">
                        {['Indoor Studio', 'Outdoor tourist sites', "Client's location", 'Indoor and Outdoor'].map((option) => (
                          <label key={option} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="shootingLocation"
                              value={option}
                              checked={formData.shootingLocation?.includes(option)}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* When do you expect to start shooting? */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        When do you expect to start shooting?
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="date"
                          name="shootingDate"
                          value={formData.shootingDate}
                          onChange={handleInputChange}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 transition-colors text-base date-input"
                          style={{ colorScheme: 'light' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-600 text-white py-2 md:py-3 px-4 md:px-4 rounded-full font-medium text-base hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 group shadow-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span className='text-base md:text-lg'>Book a consultation</span>
                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center ml-1">
                          <FaArrowRight className="text-blue-600 text-xs group-hover:translate-x-0.5 transition-transform duration-300" />
                        </div>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>


        </div>
      </main>

      <Footer />
      
      <style jsx>{`
        /* Custom date input styling */
        .date-input {
          position: relative;
        }
        
        /* Hide default calendar icon and reposition */
        .date-input::-webkit-calendar-picker-indicator {
          position: absolute;
          left: 0;
          right: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          cursor: pointer;
          opacity: 0;
        }
        
        /* Style the date text fields */
        .date-input::-webkit-datetime-edit {
          padding: 0;
          font-size: 1rem;
        }
        
        .date-input::-webkit-datetime-edit-fields-wrapper {
          padding: 0;
        }
        
        .date-input::-webkit-datetime-edit-text {
          color: #6B7280;
          padding: 0 0.125rem;
        }
        
        .date-input::-webkit-datetime-edit-month-field,
        .date-input::-webkit-datetime-edit-day-field,
        .date-input::-webkit-datetime-edit-year-field {
          color: #111827;
          padding: 0.125rem 0.25rem;
          border-radius: 4px;
        }
        
        /* Focus state for date fields */
        .date-input::-webkit-datetime-edit-month-field:focus,
        .date-input::-webkit-datetime-edit-day-field:focus,
        .date-input::-webkit-datetime-edit-year-field:focus {
          background-color: #1D44E9;
          color: white;
          outline: none;
        }
        
        /* Placeholder/empty state styling */
        .date-input::-webkit-datetime-edit-text {
          color: #9CA3AF;
        }
        
        .date-input:invalid::-webkit-datetime-edit {
          color: #9CA3AF;
        }
        
        /* Focus ring color */
        .date-input:focus {
          ring-color: #1D44E9;
          border-color: #1D44E9;
        }
      `}</style>
    </div>
  );
};

export default BookConsultation;